import { h } from 'preact';
import { css, css as cssReact } from '@emotion/react';
import { defaultCoinUrl } from '@/assets/coin';
import { Grid, Row, Col } from '@/components/grid';
import { memo } from 'preact/compat';
import { useOrientation } from '@/hooks/useOrientation';

// 充值选项类型
interface RechargeOption {
  coins: number;
  price: string;
  id: string;
  defaultSelected?: boolean;
}

// 组件属性
interface RechargeGridProps {
  options: RechargeOption[];
  selectedId: string;
  currency: 'JOLI_COIN' | 'JOLI_GEM';
  onSelect?: (option: RechargeOption) => void;
  coinImageSrc?: string;
}

// 样式定义
const styles = {
  card: cssReact`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #222222;
    border-radius: 8px;
    padding: 24px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    -webkit-tap-highlight-color: transparent;
  `,
  selectedCard: cssReact`
    background: -webkit-linear-gradient(39.03deg, rgba(255, 255, 255, 0.24) 15.04%, rgba(192, 147, 255, 0.3) 73.25%) !important;
    border-color: #7e41ff !important;
    -webkit-tap-highlight-color: transparent;
  `,
  coinContainer: cssReact`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  `,
  coinIcon: cssReact`
    width: 21px;
    height: 21px;
    margin-right: 4px;
  `,
  gemIcon: cssReact`
    width: 25px;
    height: 25px;
    margin-right: 4px;
  `,
  coinValue: cssReact`
    font-size: 20px;
    font-weight: bold;
    color: white;
  `,
  price: cssReact`
    font-size: 16px;
    color: #C093FF;
    font-weight: bold;
  `
};

const chargeItemContainer = cssReact`
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 1/1; 
`;

const chargeItemInner = cssReact`
  aspect-ratio: 1/1; // 强制 1:1 的宽高比
  display: flex;
  flex-direction: column;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
`;

const ChargeItem = (params: {
  option: RechargeOption;
  selectedId: string;
  coinImageSrc: string;
  currency: 'JOLI_COIN' | 'JOLI_GEM';
  handleSelect: (option: RechargeOption) => void;
}) => {
  return (
    <div style={chargeItemContainer.styles}>
      <div style={chargeItemInner.styles}>
        <div
          style={`${styles.card.styles} ${
            params.option.id === params.selectedId ? styles.selectedCard.styles : ''
          }`}
          onClick={() => params.handleSelect(params.option)}
        >
          <div style={styles.coinContainer.styles}>
            <img
              src={params.coinImageSrc}
              alt="Coin"
              style={params.currency === 'JOLI_GEM' ? styles.gemIcon.styles : styles.coinIcon.styles}
            />
            <span style={styles.coinValue.styles}>{params.option.coins.toLocaleString()}</span>
          </div>
          <div style={styles.price.styles}>{params.option.price}</div>
        </div>
      </div>
    </div>
  );
};

const RechargeGridInner: React.FC<RechargeGridProps> = ({
  options,
  selectedId,
  onSelect,
  currency,
  coinImageSrc = defaultCoinUrl
}) => {
  const orientation = useOrientation();

  const handleSelect = (option: RechargeOption) => {
    onSelect?.(option);
  };

  return (
    <Grid className="w-full mx-auto flex-grow overflow-y-auto" style={{ padding: '0px' }}>
      <Row
        gutter={[10, 10]}
        style={{
          display: 'flex',
          'flex-wrap': 'wrap'
        }}
      >
        {options.map((option) => (
          <Col span={orientation === 'landscape' ? 4 : 8} key={option.id}>
            <ChargeItem
              currency={currency}
              option={option}
              selectedId={selectedId}
              coinImageSrc={coinImageSrc}
              handleSelect={handleSelect}
            />
          </Col>
        ))}
      </Row>
    </Grid>
  );
};

export const ChargeGrid = memo(RechargeGridInner);
